import React from 'react';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider,
  Select, MenuItem, Avatar, Tooltip, IconButton, Chip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import type { TenantView } from './api';
import { useCustomizer } from './ThemeCustomizer';

const DRAWER_WIDTH = 256;

export interface NavItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}
export interface NavGroup {
  heading: string;
  items: NavItem[];
}

/** Enterprise admin frame: a permanent left rail (brand + grouped nav + user
 *  footer) and a top bar (section title + tenant switcher). Browsing and
 *  managing views render as `children`; focused editors take over the screen. */
export function Layout({
  groups, activeKey, onNav, title, actions, tenants, tenant, onSwitchTenant, email, onLogout, children,
}: {
  groups: NavGroup[];
  activeKey: string;
  onNav: (key: string) => void;
  title: string;
  actions?: React.ReactNode;
  tenants: TenantView[];
  tenant: TenantView;
  email?: string;
  onSwitchTenant: (id: string) => void;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  const initials = (email ?? '?').slice(0, 2).toUpperCase();
  const customizer = useCustomizer();
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none',
            bgcolor: '#0f172a', color: '#e2e8f0', display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {/* Brand */}
        <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: 'primary.main', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 15, color: '#fff' }}>A</Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.1 }}>Atlas CMS</Typography>
            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Content platform</Typography>
          </Box>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* Nav groups */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 1 }}>
          {groups.map((g) => (
            <Box key={g.heading} sx={{ mb: 1 }}>
              <Typography sx={{ px: 2.5, pt: 1.5, pb: 0.5, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, color: '#64748b', textTransform: 'uppercase' }}>
                {g.heading}
              </Typography>
              <List dense disablePadding>
                {g.items.map((it) => {
                  const active = it.key === activeKey;
                  return (
                    <ListItemButton
                      key={it.key}
                      selected={active}
                      onClick={() => onNav(it.key)}
                      sx={{
                        mx: 1.25, my: 0.25, borderRadius: 1.5, py: 0.75,
                        color: active ? '#fff' : '#cbd5e1',
                        '&.Mui-selected': { bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } },
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{it.icon}</ListItemIcon>
                      <ListItemText primary={it.label} primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 700 : 500 }} />
                      {typeof it.badge === 'number' && it.badge > 0 ? (
                        <Chip size="small" label={it.badge} sx={{ height: 18, fontSize: 11, bgcolor: active ? 'rgba(255,255,255,0.25)' : 'primary.main', color: '#fff' }} />
                      ) : null}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>

        {/* User footer */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'primary.main' }}>{initials}</Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 12.5, color: '#e2e8f0' }}>{email ?? 'Signed in'}</Typography>
            <Typography noWrap sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{tenant.role.toLowerCase().replace('_', ' ')}</Typography>
          </Box>
          <Tooltip title="Log out">
            <IconButton size="small" onClick={onLogout} sx={{ color: '#94a3b8' }}><LogoutIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </Drawer>

      {/* Main column */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Box sx={{
          height: 60, px: 3, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2,
          bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>{title}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {actions}
          <Tooltip title="Customize theme">
            <IconButton onClick={customizer.open} size="small"><PaletteOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Select
            size="small" value={tenant.tenantId} onChange={(e) => onSwitchTenant(e.target.value)}
            sx={{ minWidth: 170 }}
          >
            {tenants.map((t) => (
              <MenuItem key={t.tenantId} value={t.tenantId}>{t.name}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>{children}</Box>
      </Box>
    </Box>
  );
}
